'use client';

import { Box, Button, Flex, Text } from '@chakra-ui/react';
import type { CSSProperties, PointerEvent } from 'react';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import type { PreviewAnnotationController } from './usePreviewAnnotation';

export function PreviewAnnotationOverlay({
  annotation,
  style,
}: {
  annotation: PreviewAnnotationController;
  style?: CSSProperties;
}) {
  if (!annotation.active) return null;
  const snapshot = annotation.snapshot;
  const pointFor = (event: PointerEvent<SVGSVGElement>, clamp: boolean) => {
    if (!snapshot) return null;
    const rect = event.currentTarget.getBoundingClientRect();
    const scale = Math.min(
      rect.width / snapshot.viewport.width,
      rect.height / snapshot.viewport.height
    );
    const width = snapshot.viewport.width * scale;
    const height = snapshot.viewport.height * scale;
    const x = (event.clientX - rect.left - (rect.width - width) / 2) / width;
    const y = (event.clientY - rect.top - (rect.height - height) / 2) / height;
    if (!clamp && (x < 0 || x > 1 || y < 0 || y > 1)) return null;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  };
  return (
    <Box
      data-preview-annotation-overlay
      position="absolute"
      inset="0"
      zIndex="5"
      bg="#edece7"
      style={style}
    >
      {snapshot ? (
        <svg
          role="img"
          aria-label="Draw on the frozen preview with a mouse, pen, or touch. Explain the change in chat."
          viewBox={`0 0 ${snapshot.viewport.width} ${snapshot.viewport.height}`}
          preserveAspectRatio="xMidYMid meet"
          width="100%"
          height="100%"
          style={{ display: 'block', touchAction: 'none', cursor: 'crosshair' }}
          onPointerDown={event => {
            if (event.button !== 0) return;
            const point = pointFor(event, false);
            if (point && annotation.beginStroke(point, event.pointerId)) {
              event.preventDefault();
              event.currentTarget.setPointerCapture(event.pointerId);
            }
          }}
          onPointerMove={event => {
            const point = pointFor(event, true);
            if (point) annotation.moveStroke(point, event.pointerId);
          }}
          onPointerUp={event => {
            const point = pointFor(event, true);
            if (point) annotation.moveStroke(point, event.pointerId);
            annotation.endStroke(event.pointerId);
            if (event.currentTarget.hasPointerCapture(event.pointerId))
              event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={event => annotation.endStroke(event.pointerId)}
          onLostPointerCapture={event => annotation.endStroke(event.pointerId)}
        >
          <image
            href={`data:image/jpeg;base64,${snapshot.base64Data}`}
            width={snapshot.viewport.width}
            height={snapshot.viewport.height}
          />
          {annotation.strokes.map((stroke, index) =>
            stroke.points.length === 1 ? (
              <circle
                key={index}
                cx={stroke.points[0].x * snapshot.viewport.width}
                cy={stroke.points[0].y * snapshot.viewport.height}
                r={stroke.width / 2}
                fill={stroke.color}
              />
            ) : (
              <polyline
                key={index}
                points={stroke.points
                  .map(
                    point =>
                      `${point.x * snapshot.viewport.width},${point.y * snapshot.viewport.height}`
                  )
                  .join(' ')}
                fill="none"
                stroke={stroke.color}
                strokeWidth={stroke.width}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )
          )}
        </svg>
      ) : (
        <Flex h="full" align="center" justify="center" direction="column" gap="3" px="5">
          {annotation.busy ? (
            <>
              <Spinner size="sm" />
              <Text fontSize="sm">Capturing preview…</Text>
            </>
          ) : (
            <>
              <Text role="alert" fontSize="sm" textAlign="center">
                {annotation.error || 'Capture unavailable.'}
              </Text>
              <Button size="sm" variant="outline" onClick={() => void annotation.start()}>
                Retry capture
              </Button>
            </>
          )}
        </Flex>
      )}
      {snapshot ? (
        <Box
          position="absolute"
          bottom="3"
          left="3"
          right="3"
          pointerEvents="none"
          textAlign="center"
        >
          <Text
            as="span"
            fontSize="xs"
            bg="white"
            color="gray.800"
            borderRadius="md"
            px="3"
            py="1.5"
            shadow="sm"
            role={annotation.error ? 'alert' : 'status'}
          >
            {annotation.error || 'Draw here, then describe the change in chat.'}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}
